/**
 * Confetti — Lightweight confetti burst using Animated API.
 * No external deps. Renders falling colored rectangles.
 */
import { useEffect, useRef } from 'react';
import { View, Animated, StyleSheet, useWindowDimensions } from 'react-native';

const COLORS = ['#C5A044', '#E8D48B', '#FFFFFF', '#F5C518', '#FFD700', '#D4AF37'];
const PIECE_COUNT = 60;

interface Piece {
  xFraction: number; // 0-1, multiplied by screen width at render time
  delay: number;
  color: string;
  size: number;
  rotation: number;
}

function generatePieces(): Piece[] {
  return Array.from({ length: PIECE_COUNT }, () => ({
    xFraction: Math.random(),
    delay: Math.random() * 800,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
    size: 6 + Math.random() * 6,
    rotation: Math.random() * 360,
  }));
}

interface Props {
  /** Whether to trigger the confetti */
  active: boolean;
}

export default function Confetti({ active }: Props) {
  const { width: SCREEN_W, height: SCREEN_H } = useWindowDimensions();
  const pieces = useRef(generatePieces()).current;
  const anims = useRef(pieces.map(() => new Animated.Value(0))).current;

  useEffect(() => {
    if (!active) return;

    const animations = anims.map((anim, i) =>
      Animated.timing(anim, {
        toValue: 1,
        duration: 2000 + Math.random() * 1000,
        delay: pieces[i].delay,
        useNativeDriver: true,
      })
    );

    Animated.parallel(animations).start();
  }, [active]);

  if (!active) return null;

  return (
    <View style={StyleSheet.absoluteFill} pointerEvents="none">
      {pieces.map((piece, i) => {
        const translateY = anims[i].interpolate({
          inputRange: [0, 1],
          outputRange: [-20, SCREEN_H + 20] as [number, number],
        });
        const translateX = anims[i].interpolate({
          inputRange: [0, 0.5, 1],
          outputRange: [0, (Math.random() - 0.5) * 80, (Math.random() - 0.5) * 120],
        });
        const rotate = anims[i].interpolate({
          inputRange: [0, 1],
          outputRange: [`${piece.rotation}deg`, `${piece.rotation + 360 + Math.random() * 360}deg`],
        });
        const opacity = anims[i].interpolate({
          inputRange: [0, 0.7, 1],
          outputRange: [1, 1, 0],
        });

        return (
          <Animated.View
            key={i}
            style={[
              styles.piece,
              {
                left: piece.xFraction * SCREEN_W,
                width: piece.size,
                height: piece.size * 1.5,
                backgroundColor: piece.color,
                borderRadius: 2,
                transform: [{ translateY }, { translateX }, { rotate }],
                opacity,
              },
            ]}
          />
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  piece: {
    position: 'absolute',
    top: 0,
  },
});
